import React, { memo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PriorityHighOutlinedIcon from '@mui/icons-material/PriorityHighOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import { THEME_COLORS } from '../../../shared/constants/theme';

const ACCENT = THEME_COLORS.PRIMARY;

const calloutStyles = {
  tip: { icon: TipsAndUpdatesOutlinedIcon, color: '#2BBBAD' },
  note: { icon: InfoOutlinedIcon, color: '#5B8DEF' },
  warning: { icon: WarningAmberOutlinedIcon, color: '#E67E22' },
  success: { icon: CheckCircleOutlineIcon, color: '#27AE60' },
  important: { icon: PriorityHighOutlinedIcon, color: '#C0392B' },
};

export function GuideBlocks({ blocks, isDark, onNavigate }) {
  if (!blocks?.length) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      {blocks.map((block, i) => (
        <GuideBlock key={i} block={block} isDark={isDark} onNavigate={onNavigate} />
      ))}
    </Box>
  );
}

export default memo(GuideBlocks);

function GuideBlock({ block, isDark, onNavigate }) {
  if (!block) return null;
  const muted = isDark ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.58)';
  const text = isDark ? '#F4F6F8' : '#1A1D23';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const surface = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';

  switch (block.type) {
    case 'heading':
      return (
        <Typography
          variant={block.level === 2 ? 'h5' : 'h6'}
          sx={{ fontWeight: 700, color: text, mt: 1, letterSpacing: '-0.02em' }}
        >
          {block.text}
        </Typography>
      );

    case 'paragraph':
      return (
        <Typography sx={{ color: muted, lineHeight: 1.75, fontSize: '1.02rem' }}>
          {linkify(block.text, onNavigate, isDark)}
        </Typography>
      );

    case 'list':
      return (
        <Box>
          {block.title && (
            <Typography sx={{ fontWeight: 650, color: text, mb: 1 }}>{block.title}</Typography>
          )}
          <Box
            component={block.ordered ? 'ol' : 'ul'}
            sx={{
              m: 0,
              pl: 2.5,
              color: muted,
              '& li': { mb: 0.85, lineHeight: 1.65, pl: 0.5 },
            }}
          >
            {block.items.map((item, idx) => (
              <li key={idx}>{typeof item === 'string' ? item : item.text}</li>
            ))}
          </Box>
        </Box>
      );

    case 'steps':
      return <StepsBlock items={block.items} isDark={isDark} onNavigate={onNavigate} />;

    case 'callout': {
      const cfg = calloutStyles[block.variant] || calloutStyles.note;
      const Icon = cfg.icon;
      return (
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(cfg.color, 0.35)}`,
            background: alpha(cfg.color, isDark ? 0.12 : 0.08),
          }}
        >
          <Icon sx={{ color: cfg.color, mt: 0.15 }} />
          <Box>
            {block.title && (
              <Typography sx={{ fontWeight: 700, color: text, mb: 0.5 }}>{block.title}</Typography>
            )}
            <Typography sx={{ color: muted, lineHeight: 1.65 }}>{block.body}</Typography>
          </Box>
        </Box>
      );
    }

    case 'table':
      return (
        <Box>
          {block.caption && (
            <Typography
              variant="caption"
              sx={{ color: muted, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}
            >
              {block.caption}
            </Typography>
          )}
          <TableContainer
            sx={{
              mt: 1,
              borderRadius: 2,
              border: `1px solid ${border}`,
              background: surface,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  {block.headers.map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 700,
                        color: text,
                        borderColor: border,
                        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {block.rows.map((row, ri) => (
                  <TableRow key={ri}>
                    {row.map((cell, ci) => (
                      <TableCell key={ci} sx={{ color: muted, borderColor: border, verticalAlign: 'top' }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      );

    case 'workflow':
      return <WorkflowBlock title={block.title} nodes={block.nodes} isDark={isDark} />;

    case 'faq':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {block.items.map((item, idx) => (
            <Accordion
              key={idx}
              disableGutters
              elevation={0}
              sx={{
                border: `1px solid ${border}`,
                borderRadius: '12px !important',
                background: surface,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: muted }} />}>
                <Typography sx={{ fontWeight: 650, color: text }}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: muted, lineHeight: 1.65 }}>{item.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      );

    case 'imagePlaceholder':
      return (
        <Box
          sx={{
            borderRadius: 2,
            border: `1px dashed ${alpha(ACCENT, 0.45)}`,
            background: alpha(ACCENT, isDark ? 0.06 : 0.05),
            minHeight: 160,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 3,
          }}
        >
          <ImageOutlinedIcon sx={{ fontSize: 36, color: alpha(ACCENT, 0.9) }} />
          <Typography sx={{ fontWeight: 700, color: text }}>{block.label}</Typography>
          {block.caption && (
            <Typography variant="body2" sx={{ color: muted, textAlign: 'center' }}>
              {block.caption}
            </Typography>
          )}
        </Box>
      );

    case 'video':
      return <VideoBlock block={block} isDark={isDark} />;

    default:
      return null;
  }
}

function VideoBlock({ block, isDark }) {
  const [activated, setActivated] = useState(false);
  const border = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)';
  const text = isDark ? '#F4F6F8' : '#1A1D23';
  const muted = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.55)';
  const id = block.youtubeId;

  if (!id) return null;

  const embedUrl = `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&playsinline=1&autoplay=1`;
  const thumbUrl = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${border}`,
        overflow: 'hidden',
        background: isDark ? 'rgba(0,0,0,0.28)' : 'rgba(0,0,0,0.02)',
        contentVisibility: 'auto',
        containIntrinsicSize: '0 360px',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          pt: '56.25%',
          bgcolor: '#000',
        }}
      >
        {activated ? (
          <Box
            component="iframe"
            title={block.title || 'Roomnexa guide video'}
            src={embedUrl}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            sx={{
              position: 'absolute',
              inset: 0,
              border: 0,
              width: '100%',
              height: '100%',
            }}
          />
        ) : (
          <Box
            component="button"
            type="button"
            onClick={() => setActivated(true)}
            aria-label="Play video"
            sx={{
              position: 'absolute',
              inset: 0,
              border: 0,
              p: 0,
              cursor: 'pointer',
              backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.45)), url(${thumbUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: alpha(ACCENT, 0.95),
                color: '#04140f',
                display: 'grid',
                placeItems: 'center',
                boxShadow: `0 8px 24px ${alpha('#000', 0.45)}`,
                fontSize: 28,
                fontWeight: 800,
                lineHeight: 1,
                pl: '4px',
              }}
            >
              ▶
            </Box>
            <Typography
              sx={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                right: 12,
                color: text,
                fontWeight: 700,
                fontSize: '0.85rem',
                textShadow: '0 1px 4px rgba(0,0,0,0.8)',
              }}
            >
              {block.title || 'Watch video'}
              <Box component="span" sx={{ display: 'block', color: muted, fontWeight: 500, fontSize: '0.75rem' }}>
                Tap to load player
              </Box>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Process pipeline — responsive grid by screen size, stacked path on mobile.
 * Used by every guide's Workflow section.
 *
 * Cards per row:
 *  xs  → vertical stack (1)
 *  sm  → 2
 *  md  → 3
 *  lg+ → 4
 */
function WorkflowBlock({ title, nodes = [], isDark }) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const cols = !isSm ? 1 : isLg ? 4 : isMd ? 3 : 2;
  const muted = isDark ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.58)';
  const text = isDark ? '#F4F6F8' : '#1A1D23';
  const border = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)';
  const total = nodes.length;

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${border}`,
        overflow: 'hidden',
        background: isDark
          ? `linear-gradient(155deg, ${alpha(ACCENT, 0.12)} 0%, rgba(255,255,255,0.03) 45%, rgba(0,0,0,0.2) 100%)`
          : `linear-gradient(155deg, ${alpha(ACCENT, 0.1)} 0%, #ffffff 50%, ${alpha(ACCENT, 0.04)} 100%)`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: `1px solid ${border}`,
          background: isDark ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.55)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.25,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(ACCENT, isDark ? 0.2 : 0.14),
              color: ACCENT,
              border: `1px solid ${alpha(ACCENT, 0.35)}`,
              flexShrink: 0,
            }}
          >
            <FlagOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: muted,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                lineHeight: 1.2,
              }}
            >
              Process flow
            </Typography>
            <Typography
              sx={{
                fontWeight: 750,
                color: text,
                fontSize: '1rem',
                letterSpacing: '-0.02em',
                lineHeight: 1.3,
              }}
            >
              {title || 'Workflow'}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.15,
            py: 0.55,
            borderRadius: 999,
            border: `1px solid ${alpha(ACCENT, 0.35)}`,
            bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.08),
            color: ACCENT,
            fontWeight: 750,
            fontSize: '0.75rem',
          }}
        >
          {total} {total === 1 ? 'stage' : 'stages'}
        </Box>
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2.25 }, py: { xs: 2, sm: 2.5 } }}>
        {/* sm+: responsive grid pipeline */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'grid' },
            gridTemplateColumns: {
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            columnGap: { sm: 1.25, md: 1.5 },
            rowGap: { sm: 2.25, md: 2.5 },
          }}
        >
          {nodes.map((node, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === total - 1;
            const isLastInRow = (idx + 1) % cols === 0;
            const showRight = !isLast && !isLastInRow;
            const showDown = !isLast && isLastInRow;
            const stageLabel = isFirst ? 'Start' : isLast ? 'Finish' : `Stage ${idx + 1}`;

            return (
              <Box
                key={idx}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 108,
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    border: `1px solid ${
                      isFirst || isLast ? alpha(ACCENT, 0.45) : border
                    }`,
                    background: isFirst
                      ? alpha(ACCENT, isDark ? 0.16 : 0.12)
                      : isLast
                        ? alpha('#27AE60', isDark ? 0.14 : 0.1)
                        : isDark
                          ? 'rgba(0,0,0,0.28)'
                          : 'rgba(255,255,255,0.9)',
                    boxShadow: isDark
                      ? 'none'
                      : `0 6px 16px ${alpha('#0F172A', 0.05)}`,
                    overflow: 'hidden',
                    transition: 'transform .15s ease, border-color .15s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      borderColor: alpha(ACCENT, 0.55),
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.25,
                      py: 0.85,
                      borderBottom: `1px solid ${border}`,
                      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 11,
                        fontWeight: 800,
                        color: isLast || isFirst ? '#04140f' : text,
                        bgcolor: isLast
                          ? '#27AE60'
                          : isFirst
                            ? ACCENT
                            : alpha(ACCENT, isDark ? 0.22 : 0.16),
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 750,
                        letterSpacing: 0.45,
                        textTransform: 'uppercase',
                        color: isLast ? '#27AE60' : isFirst ? ACCENT : muted,
                      }}
                    >
                      {stageLabel}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.35,
                      py: 1.15,
                    }}
                  >
                    <Typography
                      sx={{
                        color: text,
                        fontWeight: 650,
                        fontSize: '0.86rem',
                        lineHeight: 1.4,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {node}
                    </Typography>
                  </Box>
                </Box>

                {showRight && (
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: { sm: -14, md: -16 },
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      color: alpha(ACCENT, 0.9),
                      zIndex: 2,
                      pointerEvents: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 2,
                        bgcolor: alpha(ACCENT, 0.45),
                        borderRadius: 1,
                      }}
                    />
                    <ArrowForwardIcon sx={{ fontSize: 15, ml: -0.2 }} />
                  </Box>
                )}

                {showDown && (
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      bottom: { sm: -22, md: -24 },
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      color: alpha(ACCENT, 0.9),
                      zIndex: 2,
                      pointerEvents: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        width: 2,
                        height: 8,
                        bgcolor: alpha(ACCENT, 0.45),
                        borderRadius: 1,
                      }}
                    />
                    <ArrowDownwardIcon sx={{ fontSize: 15, mt: -0.2 }} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Mobile: vertical path with connectors */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column' }}>
          {nodes.map((node, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === total - 1;
            const stageLabel = isFirst ? 'Start' : isLast ? 'Finish' : `Stage ${idx + 1}`;

            return (
              <Box key={idx} sx={{ display: 'flex', gap: 1.25 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: 28,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 12,
                      fontWeight: 800,
                      color: '#04140f',
                      bgcolor: isLast ? '#27AE60' : ACCENT,
                      zIndex: 1,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  {!isLast && (
                    <Box
                      sx={{
                        flex: 1,
                        width: 2,
                        minHeight: 18,
                        my: 0.4,
                        borderRadius: 1,
                        bgcolor: alpha(ACCENT, 0.35),
                      }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    mb: isLast ? 0 : 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${
                      isFirst || isLast ? alpha(ACCENT, 0.4) : border
                    }`,
                    background: isDark ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.9)',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 750,
                      letterSpacing: 0.45,
                      textTransform: 'uppercase',
                      color: isLast ? '#27AE60' : isFirst ? ACCENT : muted,
                      mb: 0.4,
                    }}
                  >
                    {stageLabel}
                  </Typography>
                  <Typography
                    sx={{
                      color: text,
                      fontWeight: 650,
                      fontSize: '0.9rem',
                      lineHeight: 1.45,
                    }}
                  >
                    {node}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

/** High-visibility procedure steps — used by every guide's step-by-step section */
function StepsBlock({ items = [], isDark, onNavigate }) {
  const muted = isDark ? 'rgba(255,255,255,0.62)' : 'rgba(0,0,0,0.58)';
  const text = isDark ? '#F4F6F8' : '#1A1D23';
  const border = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.1)';
  const total = items.length;

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${border}`,
        overflow: 'hidden',
        background: isDark
          ? `linear-gradient(180deg, ${alpha(ACCENT, 0.08)} 0%, rgba(255,255,255,0.02) 28%)`
          : `linear-gradient(180deg, ${alpha(ACCENT, 0.07)} 0%, #ffffff 28%)`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          px: { xs: 1.75, sm: 2.25 },
          py: 1.5,
          borderBottom: `1px solid ${border}`,
          bgcolor: isDark ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.65)',
        }}
      >
        <Typography
          sx={{
            fontWeight: 750,
            color: text,
            fontSize: '0.92rem',
            letterSpacing: '-0.01em',
          }}
        >
          Follow these steps in order
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1.1,
            py: 0.45,
            borderRadius: 999,
            border: `1px solid ${alpha(ACCENT, 0.4)}`,
            bgcolor: alpha(ACCENT, isDark ? 0.14 : 0.1),
            color: ACCENT,
            fontWeight: 800,
            fontSize: '0.72rem',
            letterSpacing: 0.3,
          }}
        >
          {total} {total === 1 ? 'step' : 'steps'}
        </Box>
      </Box>

      <Box
        component="ol"
        sx={{
          m: 0,
          p: { xs: 1.25, sm: 1.5 },
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.1,
        }}
      >
        {items.map((item, idx) => {
          const step = typeof item === 'string' ? { text: item } : item;
          const isFirst = idx === 0;
          const isLast = idx === total - 1;
          const label = step.title || null;
          const body = step.text || '';
          const num = String(idx + 1).padStart(2, '0');

          return (
            <Box
              component="li"
              key={idx}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '44px 1fr', sm: '52px 1fr' },
                gap: { xs: 1.25, sm: 1.5 },
                alignItems: 'stretch',
                p: { xs: 1.25, sm: 1.5 },
                borderRadius: 2,
                border: `1px solid ${
                  isFirst || isLast ? alpha(ACCENT, 0.4) : border
                }`,
                bgcolor: isDark ? 'rgba(0,0,0,0.32)' : 'rgba(255,255,255,0.92)',
                boxShadow: isDark ? 'none' : `0 4px 14px ${alpha('#0F172A', 0.04)}`,
                transition: 'border-color .15s ease, transform .15s ease, background-color .15s ease',
                '&:hover': {
                  borderColor: alpha(ACCENT, 0.55),
                  transform: 'translateY(-1px)',
                  bgcolor: isDark ? alpha(ACCENT, 0.08) : alpha(ACCENT, 0.05),
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, sm: 44 },
                    height: { xs: 40, sm: 44 },
                    borderRadius: 1.5,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    fontWeight: 900,
                    fontSize: { xs: '0.85rem', sm: '0.92rem' },
                    letterSpacing: '-0.03em',
                    color: '#04140f',
                    background: isLast
                      ? 'linear-gradient(145deg, #34d399 0%, #27AE60 100%)'
                      : `linear-gradient(145deg, ${ACCENT} 0%, ${THEME_COLORS.PRIMARY_HOVER} 100%)`,
                    boxShadow: `0 0 0 1px ${alpha(ACCENT, 0.35)}, 0 6px 14px ${alpha(
                      ACCENT,
                      isDark ? 0.25 : 0.28
                    )}`,
                  }}
                >
                  {num}
                </Box>
                {!isLast && (
                  <Box
                    aria-hidden
                    sx={{
                      flex: 1,
                      width: 2,
                      minHeight: 12,
                      borderRadius: 1,
                      background: `linear-gradient(180deg, ${alpha(ACCENT, 0.7)}, ${alpha(
                        ACCENT,
                        0.15
                      )})`,
                    }}
                  />
                )}
              </Box>

              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.45 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 800,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      color: isLast ? '#34d399' : ACCENT,
                      lineHeight: 1.2,
                    }}
                  >
                    {isFirst ? 'Start here' : isLast ? 'Finish' : `Step ${idx + 1}`}
                  </Typography>
                  {label && (
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 650,
                        color: muted,
                        lineHeight: 1.2,
                      }}
                    >
                      · {label}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    color: text,
                    lineHeight: 1.65,
                    fontSize: { xs: '0.95rem', sm: '1.02rem' },
                    fontWeight: 500,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {linkify(body, onNavigate, isDark)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** Very light [[guide-id|label]] link syntax in paragraphs */
function linkify(text, onNavigate, isDark) {
  if (!text || !onNavigate) return text;
  const parts = String(text).split(/(\[\[[^\]]+\]\])/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => {
    const m = part.match(/^\[\[([^\]|]+)(?:\|([^\]]+))?\]\]$/);
    if (!m) return part;
    const id = m[1].trim();
    const label = (m[2] || id).trim();
    return (
      <Box
        key={i}
        component="button"
        type="button"
        onClick={() => onNavigate(id)}
        sx={{
          display: 'inline',
          p: 0,
          m: 0,
          border: 0,
          background: 'none',
          color: ACCENT,
          cursor: 'pointer',
          font: 'inherit',
          textDecoration: 'underline',
          textUnderlineOffset: 3,
          '&:hover': { color: isDark ? '#7af0cd' : THEME_COLORS.PRIMARY_HOVER },
        }}
      >
        {label}
      </Box>
    );
  });
}
